import type { ReactNode } from 'react';
import { cn } from './cn';

type StrokeColor = string | { from: string; to: string };

/** Ant Design `Progress` replacement — line (default) or circle. */
export function Progress({
  percent = 0, status, strokeColor, showInfo = true, size, type = 'line', className, format, width = 120, strokeWidth,
}: {
  percent?: number;
  status?: 'success' | 'exception' | 'active' | 'normal';
  strokeColor?: StrokeColor;
  showInfo?: boolean;
  size?: 'small' | 'default';
  type?: 'line' | 'circle' | 'dashboard';
  className?: string;
  format?: (pct: number) => ReactNode;
  width?: number;
  strokeWidth?: number;
}) {
  const pct = Math.max(0, Math.min(100, Math.round(percent)));
  const base = status === 'exception' ? 'var(--color-danger-500)' : status === 'success' ? 'var(--color-brand-600)' : 'var(--color-brand-500)';
  const fill = typeof strokeColor === 'string' ? strokeColor : strokeColor ? `linear-gradient(90deg, ${strokeColor.from}, ${strokeColor.to})` : base;
  const info: ReactNode = format ? format(pct) : status === 'success' ? '✓' : status === 'exception' ? '✕' : `${pct}%`;

  if (type === 'circle' || type === 'dashboard') {
    const sw = strokeWidth ?? 8;
    const r = (width - sw) / 2;
    const c = 2 * Math.PI * r;
    const solid = typeof strokeColor === 'string' ? strokeColor : base;
    return (
      <div className={cn('relative inline-grid place-items-center', className)} style={{ width, height: width }}>
        <svg width={width} height={width} className="-rotate-90">
          <circle cx={width / 2} cy={width / 2} r={r} fill="none" stroke="var(--gb-surface-3)" strokeWidth={sw} />
          <circle cx={width / 2} cy={width / 2} r={r} fill="none" stroke={solid} strokeWidth={sw} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - (c * pct) / 100} className="transition-[stroke-dashoffset] duration-500" />
        </svg>
        {showInfo && <span className="absolute type-body-strong text-content">{info}</span>}
      </div>
    );
  }

  const h = size === 'small' ? 'h-1.5' : 'h-2';
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className={cn('relative flex-1 overflow-hidden rounded-full bg-surface-3', h)}>
        <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, background: fill }} />
      </div>
      {showInfo && <span className="min-w-9 shrink-0 text-right type-label text-muted">{info}</span>}
    </div>
  );
}
